import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Breadcrumb, Button, Input, Switch, Tag } from "antd";
import moment from "moment";
import Loader from "../../components/loader/loader";
import { mainIcon } from "../../assets";
import { callApi } from "../../api/apiCaller";
import { routes } from "../../api/routes";
import { GreenNotify, RedNotify } from "../../helper/helper";
import "./notificationSettings.css";

const NOOP = () => {};

const EMPTY_TEMPLATE = {
  titleArabic: "",
  titleEnglish: "",
  contentArabic: "",
  contentEnglish: "",
};

const MAX_CONTENT_LENGTH = 240;

const TEMPLATE_SECTIONS = {
  offers: {
    key: "offers",
    tabLabel: "Offers Notification Template",
    tabHint: "Automatic offer uploads",
    kicker: "Offers template",
    title: "Offers Notification Template",
    description: "This bilingual copy is used automatically when offers are uploaded.",
    previewCaption: "Auto-sent for offer uploads",
    previewFooter: "Offer upload triggers use this template automatically.",
    route: routes.updateOffersNotificationTemplate,
    saveSuccessMessage: "Offers notification template saved successfully.",
    fields: [
      {
        field: "titleArabic",
        label: "Arabic Title",
        placeholder: "حدثنا العروض",
        hint: "Short Arabic title shown first in the push notification.",
      },
      {
        field: "titleEnglish",
        label: "English Title",
        placeholder: "Offers updated",
        hint: "Short English title shown alongside the Arabic line.",
      },
      {
        field: "contentArabic",
        label: "Arabic Content",
        placeholder: "حدثنا العروض، اطلع عليها الآن في تطبيق جاك.",
        hint: "Arabic body text users read in the notification.",
        multiline: true,
      },
      {
        field: "contentEnglish",
        label: "English Content",
        placeholder: "Offers updated, open Jak app to see them!",
        hint: "English body text users read in the notification.",
        multiline: true,
      },
    ],
  },
  custom: {
    key: "custom",
    tabLabel: "Custom Notification Template",
    tabHint: "Manual admin sends",
    kicker: "Custom template",
    title: "Custom Notification Template",
    description: "Use this bilingual copy for direct notifications sent from the admin panel.",
    previewCaption: "Used for direct admin sends",
    previewFooter: "Direct sends go to opted-in users.",
    route: routes.updateCustomNotificationTemplate,
    saveSuccessMessage: "Custom notification template saved successfully.",
    fields: [
      {
        field: "titleArabic",
        label: "Arabic Title",
        placeholder: "حدثنا العروض",
        hint: "Arabic title for direct customer notifications.",
      },
      {
        field: "titleEnglish",
        label: "English Title",
        placeholder: "Offers updated",
        hint: "English title for direct customer notifications.",
      },
      {
        field: "contentArabic",
        label: "Arabic Content",
        placeholder: "حدثنا العروض، اطلع عليها الآن في تطبيق جاك.",
        hint: "Arabic body text for direct customer notifications.",
        multiline: true,
      },
      {
        field: "contentEnglish",
        label: "English Content",
        placeholder: "Offers updated, open Jak app to see them!",
        hint: "English body text for direct customer notifications.",
        multiline: true,
      },
    ],
  },
};

const cleanLineText = (value) =>
  typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";

const cleanParagraphText = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim().replace(/\r\n/g, "\n");
};

const normalizeTemplateState = (template) => ({
  ...EMPTY_TEMPLATE,
  ...template,
  titleArabic: template?.titleArabic ?? "",
  titleEnglish: template?.titleEnglish ?? "",
  contentArabic: template?.contentArabic ?? "",
  contentEnglish: template?.contentEnglish ?? "",
});

const extractTemplatePayload = (template) => ({
  titleArabic: cleanLineText(template?.titleArabic),
  titleEnglish: cleanLineText(template?.titleEnglish),
  contentArabic: cleanParagraphText(template?.contentArabic),
  contentEnglish: cleanParagraphText(template?.contentEnglish),
});

const isTemplateComplete = (template) => {
  const payload = extractTemplatePayload(template);

  return Boolean(
    payload.titleArabic &&
      payload.titleEnglish &&
      payload.contentArabic &&
      payload.contentEnglish
  );
};

const composeTemplateTitle = (template) => {
  const payload = extractTemplatePayload(template);
  return [payload.titleArabic, payload.titleEnglish].filter(Boolean).join(" ");
};

const composeTemplateBody = (template) => {
  const payload = extractTemplatePayload(template);
  return [payload.contentArabic, payload.contentEnglish].filter(Boolean).join("\n");
};

const MAX_TITLE_LENGTH = 80;

const formatLastSentAt = (value) => {
  if (!value) {
    return "Not sent yet";
  }

  const parsed = moment(value);

  if (!parsed.isValid()) {
    return "Not sent yet";
  }

  return parsed.format("DD MMM YYYY, hh:mm A");
};

const TemplateField = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  hint,
  maxLength,
  multiline = false,
}) => (
  <div className="notification-field">
    <label htmlFor={id}>{label}</label>
    {multiline ? (
      <Input.TextArea
        id={id}
        value={value}
        onChange={onChange}
        maxLength={maxLength}
        showCount
        placeholder={placeholder}
        className="notification-textarea"
        autoSize={{ minRows: 5, maxRows: 8 }}
        dir="auto"
      />
    ) : (
      <Input
        id={id}
        value={value}
        onChange={onChange}
        maxLength={maxLength}
        showCount
        placeholder={placeholder}
        className="notification-input"
        size="large"
        dir="auto"
      />
    )}
    {/* {hint && (
      <div className="notification-field-hint">
        <span>{hint}</span>
      </div>
    )} */}
  </div>
);

const PhonePreview = ({ title, body, footerText }) => (
  <div className="phone-shell">
    <div className="phone-frame">
      <div className="phone-topbar">
        <span>9:41</span>
        <span className="phone-live-dot">Live preview</span>
      </div>

      <div className="phone-notification-card" style={{marginTop:'20px'}}>
        <img className="phone-notification-icon" src={mainIcon} alt="JAK Logo" />
        <div className="phone-notification-copy">
          <span className="phone-notification-label">Notification preview</span>
          <h3>{title || "Notification title preview"}</h3>
          <p>{body || "Your bilingual message preview will appear here."}</p>
        </div>
      </div>

      <div className="phone-footer">
        <span>{footerText}</span>
        <strong>{moment().format("hh:mm A")}</strong>
      </div>
    </div>
  </div>
);

const NotificationSettings = () => {
  const isMountedRef = useRef(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isSavingSwitch, setIsSavingSwitch] = useState(false);
  const [isSavingOffers, setIsSavingOffers] = useState(false);
  const [isSavingCustom, setIsSavingCustom] = useState(false);
  const [isSendingCustom, setIsSendingCustom] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [hasLoaded, setHasLoaded] = useState(false);
  const [activeTemplateTab, setActiveTemplateTab] = useState("offers");
  const [setting, setSetting] = useState({ enabled: true });
  const [offersTemplate, setOffersTemplate] = useState(EMPTY_TEMPLATE);
  const [customTemplate, setCustomTemplate] = useState(EMPTY_TEMPLATE);
  const [offersError, setOffersError] = useState("");
  const [customError, setCustomError] = useState("");
  const [lastCustomSend, setLastCustomSend] = useState(null);

  const setPageLoadingSafely = useCallback((value) => {
    if (isMountedRef.current) {
      setIsPageLoading(value);
    }
  }, []);

  const applyOverviewResponse = useCallback((response) => {
    const payload = response?.data?.data ?? response?.data ?? null;

    if (!payload) {
      throw new Error("Notification settings data was not returned.");
    }

    const notificationSwitch =
      payload?.notificationSetting ?? payload?.switchSetting ?? payload?.setting;
    const offers =
      payload?.offersTemplate ?? payload?.templates?.offers ?? EMPTY_TEMPLATE;
    const custom =
      payload?.customTemplate ?? payload?.templates?.custom ?? EMPTY_TEMPLATE;

    setSetting({
      enabled: notificationSwitch?.enabled !== false,
    });
    setOffersTemplate(normalizeTemplateState(offers));
    setCustomTemplate(normalizeTemplateState(custom));
  }, []);

  const loadOverview = useCallback(async () => {
    const response = await callApi(
      "GET",
      routes.getNotificationSettingsOverview,
      null,
      setPageLoadingSafely,
      NOOP,
      NOOP
    );

    if (!isMountedRef.current) {
      return;
    }

    const requestFailed =
      !response ||
      response?.success === false ||
      (typeof response?.status === "number" && response.status >= 400);

    if (requestFailed) {
      setLoadError(
        response?.message ||
          "Unable to load the notification templates right now. Please try again."
      );
      return;
    }

    try {
      applyOverviewResponse(response);
      setHasLoaded(true);
      setLoadError("");
    } catch (error) {
      setHasLoaded(false);
      setOffersTemplate(EMPTY_TEMPLATE);
      setCustomTemplate(EMPTY_TEMPLATE);
      setLoadError(error?.message || "Unable to read notification settings.");
    }
  }, [applyOverviewResponse, setPageLoadingSafely]);

  useEffect(() => {
    isMountedRef.current = true;
    loadOverview();

    return () => {
      isMountedRef.current = false;
    };
  }, [loadOverview]);

  const isEnabled = setting.enabled;
  const currentStateText = loadError
    ? "Unavailable"
    : hasLoaded
      ? isEnabled
        ? "Enabled"
        : "Disabled"
      : "Loading";
  const currentStateDescription = loadError
    ? "We could not load the switch state. Retry to continue."
    : hasLoaded
      ? isEnabled
        ? "Offer uploads and custom sends can leave the backend."
        : "Turn this on to allow notification sends."
      : "Loading notification settings...";
  const currentStateTagColor = loadError ? undefined : isEnabled ? "green" : "red";

  const activeSection = TEMPLATE_SECTIONS[activeTemplateTab];
  const activeTemplate =
    activeTemplateTab === "offers" ? offersTemplate : customTemplate;
  const activeTemplateSetter =
    activeTemplateTab === "offers" ? setOffersTemplate : setCustomTemplate;
  const activeTemplateError =
    activeTemplateTab === "offers" ? offersError : customError;
  const activeTemplateSetError =
    activeTemplateTab === "offers" ? setOffersError : setCustomError;
  const activeTemplateSaving =
    activeTemplateTab === "offers" ? isSavingOffers : isSavingCustom;
  const activePreviewTitle = composeTemplateTitle(activeTemplate);
  const activePreviewBody = composeTemplateBody(activeTemplate);
  const activePreviewFooter = isEnabled
    ? activeSection.previewFooter
    : "Enable notifications to send this template.";

  const handleToggle = async (checked) => {
    if (!hasLoaded || checked === setting.enabled || isSavingSwitch) {
      return;
    }

    const previousSetting = setting;
    setSetting((current) => ({
      ...current,
      enabled: checked,
    }));
    setIsSavingSwitch(true);

    try {
      const response = await callApi(
        "PATCH",
        routes.updateBulkOfferNotificationSetting,
        { enabled: checked },
        setIsSavingSwitch,
        NOOP,
        NOOP
      );

      const requestFailed =
        !response ||
        response?.success === false ||
        (typeof response?.status === "number" && response.status >= 400);

      if (requestFailed) {
        throw new Error(response?.message || "Unable to update the setting.");
      }

      setSetting((current) => ({
        ...current,
        enabled: checked,
      }));
      GreenNotify(
        checked
          ? "Notification sending has been enabled."
          : "Notification sending has been disabled."
      );
    } catch (error) {
      setSetting(previousSetting);
      RedNotify(error?.message || "Could not save the notification setting.");
    } finally {
      if (isMountedRef.current) {
        setIsSavingSwitch(false);
      }
    }
  };

  const handleActiveFieldChange = (field) => (event) => {
    const value = event.target.value;
    activeTemplateSetter((current) => ({
      ...current,
      [field]: value,
    }));
    activeTemplateSetError("");
  };

  const saveTemplate = async ({
    route,
    template,
    setTemplate,
    setSaving,
    setError,
    successMessage,
    label,
  }) => {
    if (!hasLoaded || loadError || isSavingSwitch) {
      return;
    }

    const payload = extractTemplatePayload(template);

    if (!isTemplateComplete(payload)) {
      const message = `${label} requires Arabic and English title and content.`;
      setError(message);
      RedNotify(message);
      return;
    }

    const response = await callApi(
      "PATCH",
      route,
      payload,
      setSaving,
      NOOP,
      NOOP
    );

    const requestFailed =
      !response ||
      response?.success === false ||
      (typeof response?.status === "number" && response.status >= 400);

    if (requestFailed) {
      const message = response?.message || `Could not save the ${label.toLowerCase()}.`;
      setError(message);
      RedNotify(message);
      return;
    }

    const savedTemplate = response?.data?.data ?? response?.data ?? null;

    if (savedTemplate) {
      setTemplate(normalizeTemplateState(savedTemplate));
    }

    setError("");
    GreenNotify(response?.message || successMessage);
  };

  const handleSaveOffers = async (event) => {
    event.preventDefault();

    await saveTemplate({
      route: routes.updateOffersNotificationTemplate,
      template: offersTemplate,
      setTemplate: setOffersTemplate,
      setSaving: setIsSavingOffers,
      setError: setOffersError,
      successMessage: TEMPLATE_SECTIONS.offers.saveSuccessMessage,
      label: TEMPLATE_SECTIONS.offers.title,
    });
  };

  const handleSaveCustom = async (event) => {
    event.preventDefault();

    await saveTemplate({
      route: routes.updateCustomNotificationTemplate,
      template: customTemplate,
      setTemplate: setCustomTemplate,
      setSaving: setIsSavingCustom,
      setError: setCustomError,
      successMessage: TEMPLATE_SECTIONS.custom.saveSuccessMessage,
      label: TEMPLATE_SECTIONS.custom.title,
    });
  };

  const handleSendCustom = async () => {
    if (!hasLoaded || loadError || !isEnabled || isSendingCustom) {
      return;
    }

    const payload = extractTemplatePayload(customTemplate);

    if (!isTemplateComplete(payload)) {
      const message =
        "Custom notification template requires Arabic and English title and content.";
      setCustomError(message);
      RedNotify(message);
      return;
    }

    const response = await callApi(
      "POST",
      routes.sendCustomNotification,
      payload,
      setIsSendingCustom,
      NOOP,
      NOOP
    );

    const requestFailed =
      !response ||
      response?.success === false ||
      (typeof response?.status === "number" && response.status >= 400);

    if (requestFailed) {
      const message = response?.message || "Could not send the notification.";
      setCustomError(message);
      RedNotify(message);
      return;
    }

    const sendPayload = response?.data?.data ?? response?.data ?? {};
    const savedTemplate = sendPayload?.template ?? null;

    if (savedTemplate) {
      setCustomTemplate(normalizeTemplateState(savedTemplate));
    }

    setLastCustomSend({
      title: composeTemplateTitle(savedTemplate ?? payload),
      description: composeTemplateBody(savedTemplate ?? payload),
      receiverCount: sendPayload?.receiverCount || 0,
      tokenCount: sendPayload?.tokenCount || 0,
      createdAt:
        sendPayload?.notification?.createdAt ||
        sendPayload?.createdAt ||
        new Date().toISOString(),
    });
    setCustomError("");
    GreenNotify(response?.message || "Notification sent successfully.");
  };

  return (
    <div className="modern-page-container notification-settings-page">
      <Loader loading={isPageLoading} />

      <div className="page-header notification-hero">
        <div className="notification-hero-copy">
          <Breadcrumb
            separator="›"
            className="modern-breadcrumb"
            items={[
              { title: "Dashboard" },
              { title: <span className="breadcrumb-active">Notifications</span> },
            ]}
          />

          <span className="notification-kicker">Notification controls</span>
          <h1 className="page-title">Notification Templates</h1>
          <p className="page-subtitle">
            Switch between the offers template and the custom template while keeping
            the live mobile preview on the right.
          </p>

          <div className="notification-hero-tags">
            <Tag color={isEnabled ? "green" : "red"} className="notification-pill">
              {isEnabled ? "Sending enabled" : "Sending paused"}
            </Tag>
            <Tag color="blue" className="notification-pill">
              Opted-in users only
            </Tag>
            {/* <Tag color="gold" className="notification-pill">
              Left tabs + live preview
            </Tag> */}
          </div>
        </div>

        <div className="notification-status-card">
          <div className="notification-status-head">
            <div>
              <span className="notification-kicker">Master switch</span>
              <h3>{currentStateText}</h3>
              <p>{currentStateDescription}</p>
            </div>
            <Tag color={currentStateTagColor} className="notification-state-tag">
              {loadError ? "N/A" : isEnabled ? "ON" : "OFF"}
            </Tag>
          </div>

          <div className="notification-switch-row">
            <Switch
              checked={hasLoaded ? isEnabled : false}
              loading={isSavingSwitch}
              onChange={handleToggle}
              disabled={isPageLoading || Boolean(loadError) || !hasLoaded}
            />
            <span>
              {loadError
                ? "Retry to unlock the switch"
                : isEnabled
                  ? "Notification sends are available"
                  : "Turn this on to send notifications"}
            </span>
          </div>
        </div>
      </div>

      {loadError && (
        <Alert
          className="notification-alert"
          type="error"
          showIcon
          message="Unable to load notification templates"
          description={
            <div className="notification-alert-actions">
              <span>{loadError}</span>
              <Button type="primary" onClick={loadOverview}>
                Retry
              </Button>
            </div>
          }
        />
      )}

      <div className="notification-grid">
        <section className="notification-card notification-editor-card">
          <div className="notification-template-tabs" role="tablist" aria-label="Notification templates">
            {Object.values(TEMPLATE_SECTIONS).map((section) => {
              const isActive = activeTemplateTab === section.key;

              return (
                <button
                  key={section.key}
                  type="button"
                  className={`notification-tab-btn ${isActive ? "is-active" : ""}`}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTemplateTab(section.key)}
                >
                  <span>{section.tabLabel}</span>
                  <small>{section.tabHint}</small>
                </button>
              );
            })}
          </div>

          <div className="notification-card-head">
            {/* <span className="notification-kicker">{activeSection.kicker}</span> */}
            <h2>{activeSection.title}</h2>
            <p>{activeSection.description}</p>
          </div>

          <form
            className="notification-form"
            onSubmit={activeTemplateTab === "offers" ? handleSaveOffers : handleSaveCustom}
          >
            <div className="notification-template-grid-fields">
              {activeSection.fields.map((fieldConfig) => (
                <TemplateField
                  key={`${activeSection.key}-${fieldConfig.field}`}
                  id={`${activeSection.key}-${fieldConfig.field}`}
                  label={fieldConfig.label}
                  value={activeTemplate[fieldConfig.field]}
                  onChange={handleActiveFieldChange(fieldConfig.field)}
                  placeholder={fieldConfig.placeholder}
                  hint={fieldConfig.hint}
                  maxLength={
                    fieldConfig.multiline ? MAX_CONTENT_LENGTH : MAX_TITLE_LENGTH
                  }
                  multiline={fieldConfig.multiline}
                />
              ))}
            </div>

            <div className="notification-actions-row">
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={activeTemplateSaving}
                disabled={!hasLoaded || Boolean(loadError)}
                className="notification-save-btn"
              >
                Save Template
              </Button>

              {activeTemplateTab === "custom" && (
                <Button
                  type="primary"
                  htmlType="button"
                  size="large"
                  loading={isSendingCustom}
                  disabled={!hasLoaded || Boolean(loadError) || !isEnabled}
                  onClick={handleSendCustom}
                  className="notification-send-btn"
                >
                  Send Notification
                </Button>
              )}
            </div>

            <div className="notification-actions-copy">
              <span>{activeSection.previewFooter}</span>
            </div>

            {activeTemplateError && (
              <Alert
                type="error"
                showIcon
                className="notification-send-alert"
                message={activeTemplateError}
              />
            )}

            {activeTemplateTab === "custom" && lastCustomSend && (
              <div className="notification-last-sent">
                <span className="notification-kicker">Last send</span>
                <strong>{lastCustomSend.title}</strong>
                <p>{lastCustomSend.description}</p>
                <p className="notification-last-sent-stats">
                  {lastCustomSend.receiverCount} users targeted •{" "}
                  {lastCustomSend.tokenCount} devices reached
                </p>
                <span className="notification-last-sent-time">
                  {formatLastSentAt(lastCustomSend.createdAt)}
                </span>
              </div>
            )}
          </form>
        </section>

        <aside className="notification-card notification-preview-card">
          <div className="notification-card-head">
            <span className="notification-kicker">Mobile preview</span>
            <h2>What users will receive</h2>
            <p>{activeSection.previewCaption}</p>
          </div>

          <PhonePreview
            title={activePreviewTitle}
            body={activePreviewBody}
            footerText={activePreviewFooter}
          />

          <div className="phone-caption">
            {activeTemplateTab === "custom"
              ? "This preview updates as you edit the custom template."
              : "This preview updates as you edit the offers template."}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default NotificationSettings;
